'use client';

import * as Sentry from '@sentry/nextjs';

import { getPostHogLink } from './posthog';

/**
 * Link every browser Sentry event to its PostHog session.
 *
 * Runs once from `instrumentation-client.ts` after `Sentry.init`. The event
 * processor stamps `posthog_distinct_id` / `posthog_session_id` tags (plus a
 * `posthog` context) onto each event, so a future Sentry issue answers "what
 * was the user doing?" with a direct hop to the PostHog replay — no separate
 * lookup by timestamp. Best-effort by design: when PostHog is off, events
 * pass through untouched.
 */
export function registerPostHogSentryLink(): void {
  try {
    Sentry.addEventProcessor((event) => {
      try {
        const link = getPostHogLink();
        if (!link.distinctId && !link.sessionId) return event;
        return {
          ...event,
          contexts: {
            ...event.contexts,
            posthog: {
              ...(link.distinctId ? { distinct_id: link.distinctId } : {}),
              ...(link.sessionId ? { session_id: link.sessionId } : {}),
            },
          },
          tags: {
            ...event.tags,
            ...(link.distinctId ? { posthog_distinct_id: link.distinctId } : {}),
            ...(link.sessionId ? { posthog_session_id: link.sessionId } : {}),
          },
        };
      } catch {
        return event;
      }
    });
  } catch {
    // Sentry unavailable — browser reporting stays as-is.
  }
}
